/**
 * A assinatura digital embutida no arquivo, no quadro de verificação
 * (E74.F4; E76.F1–F4).
 *
 * Extraído de `QuadroVerificacao.tsx` na E78.F3.T02 para que o quadro por eixos
 * monte o MESMO bloco: as regras do que pode ser afirmado sobre a assinatura —
 * legado sem veredito, carimbo sem cadeia conferida, passe de revogação não
 * executado, papel do signatário — foram compradas com quatro Fases de
 * correção, e uma segunda cópia delas divergiria na primeira mudança.
 */
import { Badge, MonoData, type BadgeProps } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ResultadoExameEletronicoData } from '@/lib/exame_eletronico';
import {
  situacaoDoPasseDeRevogacao,
  type AssinaturaEmbutidaData,
  type SituacaoCarimbo,
} from '@/lib/exame_eletronico_assinatura_digital';

import { VestigioDeAssinatura } from './QuadroVerificacaoOrigem';
import { formatarInstante, SituacaoBadge, TabelaItens, Titulo } from './QuadroVerificacaoTabelas';
import { SelosDaPlataforma } from './QuadroVerificacaoSelo';

const t = ptBR.exameEletronico.quadro;

/** Texto da autoridade por `SituacaoCarimbo`; `ausente` fica de fora — sem
 * carimbo não há autoridade a relatar (F-E76.F2-01/02). */
const ESTADO_DA_AUTORIDADE: Record<Exclude<SituacaoCarimbo, 'ausente'>, string> = {
  confiavel: t.assinaturaDigitalCarimboConfirmada,
  autoridade_nao_confirmada: t.assinaturaDigitalCarimboNaoConfirmada,
  token_invalido: t.assinaturaDigitalCarimboNaoIntegro,
};

/**
 * Linha de confiabilidade do carimbo por assinatura. `null`/`undefined` é o
 * marcador de LEGADO (E76.F2): registro anterior à Fase tem carimbo mas nunca
 * checou a cadeia da TSA. `'ausente'` é "não havia carimbo". As duas NÃO
 * imprimem a linha — nunca uma alegação de cadeia não confirmada
 * (F-E76.F2-01, que tratava a string `'ausente'` como preenchida/truthy).
 */
function LinhaAutoridade({ assinatura }: { assinatura: AssinaturaEmbutidaData }) {
  const situacao = assinatura.carimbo_confiabilidade;
  if (!situacao || situacao === 'ausente') return null;
  return (
    <li>
      <span className="text-fg-dim">{t.assinaturaDigitalCarimboAutoridade}</span>{' '}
      <span className="text-fg">
        {assinatura.carimbo_autoridade ?? '—'} — {ESTADO_DA_AUTORIDADE[situacao]}
      </span>
    </li>
  );
}

/**
 * E157.F1 (ADR-0107 §2.1): a data pertinente da conferência, com a origem, e a
 * data declarada pelo signatário como declaração. `null`/ausente é o registro
 * anterior à régua v47 — nada é impresso no lugar.
 */
function LinhaInstantePertinente({ assinatura }: { assinatura: AssinaturaEmbutidaData }) {
  const ip = assinatura.instante_pertinente;
  if (!ip) return null;
  const origens: Record<string, string> = t.assinaturaDigitalInstanteOrigem;
  return (
    <li data-testid="assinatura-instante-pertinente">
      <span className="text-fg-dim">{t.assinaturaDigitalInstantePertinente}</span>{' '}
      <MonoData as="time" variant="muted" dateTime={ip.instante}>
        {formatarInstante(ip.instante, false)}
      </MonoData>{' '}
      <span className="text-fg">({origens[ip.origem] ?? '—'})</span>
      {ip.declarado_pelo_signatario ? (
        <>
          {' · '}
          <span className="text-fg-dim">{t.assinaturaDigitalDeclaradoPeloSignatario}</span>{' '}
          <MonoData as="time" variant="muted" dateTime={ip.declarado_pelo_signatario}>
            {formatarInstante(ip.declarado_pelo_signatario, false)}
          </MonoData>
        </>
      ) : null}
    </li>
  );
}

/**
 * Papel da assinatura na doutrina por papel (E76.F3). `null`/ausente — registro
 * anterior à Fase ou confronto impossível — não imprime badge: nenhuma
 * alegação de atribuição que o exame não fez (padrão F-E76.F1-04).
 */
function BadgePapel({ assinatura }: { assinatura: AssinaturaEmbutidaData }) {
  if (assinatura.do_titular == null) return null;
  return assinatura.do_titular ? (
    <Badge variant="success" data-testid="assinatura-papel-titular">
      {t.assinaturaDigitalPapelTitular}
    </Badge>
  ) : (
    <Badge variant="neutral" data-testid="assinatura-papel-terceiro">
      {t.assinaturaDigitalPapelTerceiro}
    </Badge>
  );
}

/** Rótulo da situação de revogação; valor desconhecido do backend cai no
 * fallback neutro, nunca no código cru. */
function rotuloDaRevogacao(situacao: string): string {
  const opcoes: Record<string, string> = t.assinaturaDigitalRevogacaoOpcoes;
  return opcoes[situacao] ?? '—';
}

/** Certificado e papel de UMA assinatura — as únicas linhas condicionais ao
 * certificado (F-E76.F3-03): sem ele o bloco não some, a ausência é declarada. */
function LinhasCertificado({ assinatura }: { assinatura: AssinaturaEmbutidaData }) {
  const certificado = assinatura.certificado;
  if (!certificado) {
    return (
      <li className="flex flex-wrap items-center gap-sc-2">
        <span className="text-fg-dim">{t.assinaturaDigitalCertificado}</span>{' '}
        <span className="text-fg">{t.assinaturaDigitalSemCertificado}</span>
        <BadgePapel assinatura={assinatura} />
      </li>
    );
  }
  return (
    <>
      <li className="flex flex-wrap items-center gap-sc-2">
        <span className="text-fg-dim">{t.assinaturaDigitalCertificado}</span>{' '}
        <span className="text-fg">{certificado.titular}</span>
        <BadgePapel assinatura={assinatura} />
      </li>
      {certificado.cnpj ? (
        <li>
          <span className="text-fg-dim">{t.assinaturaDigitalCnpj}</span>{' '}
          <MonoData variant="muted">{certificado.cnpj}</MonoData>
        </li>
      ) : null}
      <li>
        <span className="text-fg-dim">{t.assinaturaDigitalEmissor}</span>{' '}
        <span className="text-fg">{certificado.emissor}</span>
      </li>
      <li>
        <span className="text-fg-dim">{t.assinaturaDigitalSerie}</span>{' '}
        <MonoData variant="muted">{certificado.numero_serie}</MonoData>
      </li>
      <li className="break-all">
        <span className="text-fg-dim">{t.assinaturaDigitalImpressao}</span>{' '}
        <MonoData variant="muted">{certificado.impressao_digital}</MonoData>
      </li>
    </>
  );
}

/** O detalhe de UMA assinatura embutida: campo, certificado, papel, fatos
 * próprios (integridade e revogação, E76.F3), carimbo e algoritmos. A linha de
 * revogação depende do passe ter existido no registro E ter sido executado
 * (F-E76.F3-06): em exame legado ou com o passe `nao_executado` o valor é
 * default de schema, nunca resultado de uma conferência. */
function DetalheAssinatura({
  assinatura,
  revogacaoConferida,
}: {
  assinatura: AssinaturaEmbutidaData;
  revogacaoConferida: boolean;
}) {
  return (
    <ul
      className="space-y-sc-1 text-body-sm"
      data-testid={`quadro-assinatura-digital-${assinatura.campo}`}
    >
      <li className="flex flex-wrap items-center gap-sc-2">
        <span className="text-fg-dim">{t.assinaturaDigitalCampo}</span>{' '}
        <MonoData variant="muted">{assinatura.campo}</MonoData>
      </li>
      <LinhasCertificado assinatura={assinatura} />
      <li className="flex flex-wrap items-center gap-sc-2">
        <span className="text-fg-dim">{t.assinaturaDigitalIntegridadeAssinatura}</span>
        <SituacaoBadge situacao={assinatura.integra ? 'confere' : 'nao_confere'} />
      </li>
      {revogacaoConferida ? (
        <li>
          <span className="text-fg-dim">{t.assinaturaDigitalSituacaoRevogacao}</span>{' '}
          <span className="text-fg">{rotuloDaRevogacao(assinatura.revogacao)}</span>
        </li>
      ) : null}
      {assinatura.carimbo_em ? (
        <li>
          <span className="text-fg-dim">{t.assinaturaDigitalCarimboEm}</span>{' '}
          <MonoData as="time" variant="muted" dateTime={assinatura.carimbo_em}>
            {formatarInstante(assinatura.carimbo_em, false)}
          </MonoData>
        </li>
      ) : null}
      <LinhaInstantePertinente assinatura={assinatura} />
      <LinhaAutoridade assinatura={assinatura} />
      {assinatura.algoritmo_hash && assinatura.mecanismo_assinatura ? (
        <li>
          <span className="text-fg-dim">{t.assinaturaDigitalAlgoritmoHash}</span>{' '}
          <MonoData variant="muted">{assinatura.algoritmo_hash}</MonoData>
          {' · '}
          <span className="text-fg-dim">{t.assinaturaDigitalMecanismoAssinatura}</span>{' '}
          <MonoData variant="muted">{assinatura.mecanismo_assinatura}</MonoData>
        </li>
      ) : null}
    </ul>
  );
}

/**
 * O que o PRÓPRIO arquivo comprova (E74.F4). As demais seções repetem, já
 * conferidas, as afirmações do protocolo transcrito; esta é a única que fala do
 * documento em si — por isso ela traz junto o certificado e a limitação do
 * método, que é o que impede "cadeia confere" de ser lido como validação plena.
 * Desde a E76.F3 cada assinatura mostra também o próprio estado (papel,
 * integridade, revogação): o item agregado responde pelo alvo, e o detalhe por
 * assinatura responde pelas demais.
 */
/**
 * Badge do veredito subordinado do estágio (E76.F4, ADR-0076). Renderiza SÓ o
 * campo do backend — a UI nunca recalcula (fonte única). `null` é o exame
 * anterior ao veredito: nenhuma alegação, o bloco não aparece. Valor
 * desconhecido cai no fallback neutro, nunca no código cru.
 */
function VereditoAssinatura({
  veredito,
}: {
  veredito: ResultadoExameEletronicoData['assinatura_digital_veredito'];
}) {
  if (!veredito) return null;
  const opcoes: Record<string, string> = t.assinaturaDigitalVereditoOpcoes;
  const tons: Record<string, BadgeProps['variant']> = {
    confirmada: 'success',
    nao_confirmada: 'danger',
    nao_foi_possivel_confirmar: 'warning',
    nao_se_aplica: 'neutral',
  };
  return (
    <p
      className="flex flex-wrap items-center gap-sc-2 text-body-sm"
      data-testid="quadro-assinatura-digital-veredito"
    >
      <span className="text-fg-dim">{t.assinaturaDigitalVereditoRotulo}</span>
      <Badge variant={tons[veredito] ?? 'neutral'}>{opcoes[veredito] ?? '—'}</Badge>
    </p>
  );
}

function AssinaturaDigital({
  apurado,
  veredito,
}: {
  apurado: ResultadoExameEletronicoData['assinatura_digital'];
  veredito: ResultadoExameEletronicoData['assinatura_digital_veredito'];
}) {
  if (!apurado) {
    return (
      <section data-testid="quadro-assinatura-digital">
        <Titulo>{t.assinaturaDigital}</Titulo>
        <p className="text-body-sm text-fg-dim">{t.assinaturaDigitalNaoExaminada}</p>
      </section>
    );
  }
  // O passe de revogação comprovado pelo registro governa a frase de
  // fechamento E a linha individual de cada assinatura (E76.F1; F-E76.F3-06).
  // Só `executado` autoriza estado individual: item ausente (exame legado) e
  // item `nao_executado` (trust store sem âncoras) são registros de que a
  // conferência NÃO ocorreu — cada um dito com a sua frase, nunca com um
  // estado por assinatura que leria default de schema como fato apurado.
  const passeDeRevogacao = situacaoDoPasseDeRevogacao(apurado.itens);
  const notaDoMetodo: Record<typeof passeDeRevogacao, string> = {
    executado: t.assinaturaDigitalLimite,
    nao_executado: t.assinaturaDigitalRevogacaoNaoExecutada,
    sem_item: t.assinaturaDigitalRevogacaoNaoExaminada,
  };
  return (
    <div className="space-y-sc-2" data-testid="quadro-assinatura-digital">
      <TabelaItens
        titulo={t.assinaturaDigital}
        itens={apurado.itens}
        testId="quadro-assinatura-digital-itens"
      />
      <VereditoAssinatura veredito={veredito} />
      <VestigioDeAssinatura vestigio={apurado.vestigio} />
      <SelosDaPlataforma apurado={apurado} />
      {apurado.assinaturas.map((assinatura) => (
        <DetalheAssinatura
          key={assinatura.campo}
          assinatura={assinatura}
          revogacaoConferida={passeDeRevogacao === 'executado'}
        />
      ))}
      {apurado.assinaturas.length > 0 ? (
        <p className="text-caption text-fg-dim" data-testid="quadro-assinatura-digital-metodo">
          {notaDoMetodo[passeDeRevogacao]}
        </p>
      ) : null}
      {apurado.consulta_em_linha ? (
        <p
          className="text-caption text-fg-dim"
          data-testid="quadro-assinatura-digital-consulta-em-linha"
        >
          {t.assinaturaDigitalConsultaEmLinha}: {t.assinaturaDigitalConsultaNaoExecutada} —{' '}
          {apurado.consulta_em_linha.motivo}
        </p>
      ) : null}
    </div>
  );
}

export { AssinaturaDigital };
